<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import { Helper } from '@/mixin/helper'
import * as datasetInvalidReasonApi from '@/services/rest-api-sms/lib/dataset-invalid-reason/dataset-invalid-reason'
import * as trainingDatasetApi from '@/services/rest-api-sms/lib/training-dataset/training-dataset'
import { TRAINING_DATASET_LIMIT, buildTrainingDatasetQuery } from './lib/validation-query'

export default {
  page: {
    title: 'Validasi AI Training',
    meta: [{ name: 'description', content: appConfig.description }],
  },

  components: {
    Layout,
    PageHeader,
  },

  mixins: [Helper],

  data() {
    return {
      isLoading: false,
      isSavingAll: false,
      savingIds: {},
      reasonOptions: [],
      photos: [],
      currentPage: 1,
      perPage: TRAINING_DATASET_LIMIT,
      totalRows: 0,
      totalPages: 0,
      searchKeyword: '',
      searchDebounceTimer: null,
      activeFilter: 'all',
      filterCards: [
        {
          value: 'valid',
          label: 'Valid',
          description: 'state valid',
          icon: 'bx-check-circle',
        },
        {
          value: 'invalid',
          label: 'Invalid',
          description: 'state invalid',
          icon: 'bx-x-circle',
        },
        {
          value: 'all',
          label: 'Semua',
          description: 'semua data',
          icon: 'bx-grid-alt',
        },
      ],
      tasksType: {
        'I': "Install",
        'M': "Maintenance",
        'P': "Pindah Unit"
      }
    }
  },

  async created() {
    await this.getInvalidReasons()
    this.getTrainingDataset()
  },

  beforeDestroy() {
    clearTimeout(this.searchDebounceTimer)
  },

  computed: {
    title() {
      return 'AI Training'
    },

    pageTitle() {
      return this.$route.query.category || 'Foto Odometer'
    },

    decidedCount() {
      return this.photos.filter((photo) => photo.status).length
    },

    changedPhotos() {
      return this.photos.filter((photo) => this.hasChanged(photo))
    },

    progressPercentage() {
      if (!this.photos.length) return 0
      return Math.round((this.decidedCount / this.photos.length) * 100)
    },

    paginationStart() {
      return this.photos.length ? (this.currentPage - 1) * this.perPage + 1 : 0
    },

    paginationEnd() {
      return Math.min(this.totalRows, (this.currentPage - 1) * this.perPage + this.photos.length)
    },
  },

  methods: {
    async getTrainingDataset() {
      this.isLoading = true

      const params = buildTrainingDatasetQuery({
        category: this.pageTitle,
        currentPage: this.currentPage,
        search: this.searchKeyword,
        statusFilter: this.activeFilter,
      })
      const response = await trainingDatasetApi
        .get(params)
        .catch((error) => this.handleError(error))

      this.photos = response?.status ? this.mapPhotos(response.data?.paginated_result || []) : []
      this.totalRows = response?.status ? response.metadata?.total_count || 0 : 0
      this.totalPages = response?.status ? response.metadata?.total_page || 0 : 0
      this.currentPage = response?.status ? response.metadata?.page || this.currentPage : 1
      this.isLoading = false
    },

    async getInvalidReasons() {
      const response = await datasetInvalidReasonApi.get().catch((error) => this.handleError(error))
      this.reasonOptions = response?.status ? response.data?.paginated_result || [] : []
    },

    mapPhotos(photos) {
      return photos.map((photo) => ({
        id: photo.training_dataset_id,
        name: photo.name || '-',
        url: photo.url,
        workOrder: photo.wo_number ?? '-',
        workOrderType: photo.tasks_type ? this.tasksType[photo.tasks_type] : '-',
        status: ['valid', 'invalid'].includes(photo.state) ? photo.state : null,
        originalStatus: ['valid', 'invalid'].includes(photo.state) ? photo.state : null,
        invalidReasonId: photo.invalid_reason_id,
        originalInvalidReasonId: photo.invalid_reason_id,
        reason: this.getReasonName(photo.invalid_reason_id),
      }))
    },

    hasChanged(photo) {
      return Boolean(
        photo.status &&
          (photo.status !== photo.originalStatus ||
            photo.invalidReasonId !== photo.originalInvalidReasonId)
      )
    },

    markValid(photo) {
      photo.status = 'valid'
      photo.reason = ''
      photo.invalidReasonId = null
    },

    markInvalid(photo, reason) {
      photo.status = 'invalid'
      photo.reason = reason.name
      photo.invalidReasonId = reason.invalid_reason_id
    },

    getReasonName(reasonId) {
      return this.reasonOptions.find((reason) => reason.invalid_reason_id === reasonId)?.name || ''
    },

    getSavePayload(photo) {
      const payload = { state: photo.status }
      if (photo.status === 'invalid') payload.invalid_reason_id = photo.invalidReasonId
      return payload
    },

    reloadFromFirstPage() {
      this.currentPage = 1
      this.getTrainingDataset()
    },

    changePage(page) {
      this.currentPage = page
      this.getTrainingDataset()
    },

    changeFilter(filter) {
      if (this.activeFilter === filter) return
      this.activeFilter = filter
      this.reloadFromFirstPage()
    },

    handleSearchInput() {
      clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = setTimeout(() => {
        this.reloadFromFirstPage()
      }, 500)
    },

    async savePhoto(photo) {
      if (!this.hasChanged(photo)) return
      this.$set(this.savingIds, photo.id, true)

      const response = await trainingDatasetApi
        .update(photo.id, this.getSavePayload(photo))
        .catch((error) => this.handleError(error))

      if (response?.status) {
        photo.originalStatus = photo.status
        photo.originalInvalidReasonId = photo.invalidReasonId
        this.$toast?.success('Data berhasil disimpan')
      }

      this.$set(this.savingIds, photo.id, false)
    },

    async saveAll() {
      if (!this.changedPhotos.length) return
      this.isSavingAll = true

      const payload = this.changedPhotos.map((photo) => ({
        training_dataset_id: photo.id,
        ...this.getSavePayload(photo),
      }))
      const response = await trainingDatasetApi
        .updateBulk(payload)
        .catch((error) => this.handleError(error))

      if (response?.status) {
        this.changedPhotos.forEach((photo) => {
          photo.originalStatus = photo.status
          photo.originalInvalidReasonId = photo.invalidReasonId
        })
        this.$toast?.success('Semua perubahan berhasil disimpan')
      }

      this.isSavingAll = false
    },

    getStatusIcon(photo) {
      if (photo.status === 'valid') return 'bx-check'
      if (photo.status === 'invalid') return 'bx-x'
      return ''
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" />
    
    <div class="ai-training-validation">
      <div class="ai-training-validation__topbar">
        <h4 class="ai-training-validation__title mb-0">{{ pageTitle?.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()) }}</h4>
        <!-- <b-button
          v-if="changedPhotos.length"
          variant="primary"
          :disabled="isSavingAll"
          @click="saveAll"
        >
          <b-spinner v-if="isSavingAll" small class="me-2"></b-spinner>
          Simpan Semua
        </b-button> -->
      </div>

      <div class="card ai-training-validation__workspace">
        <div class="ai-training-validation__progress">
          <strong>{{ decidedCount }} dari {{ photos.length }} foto diputuskan</strong>
          <div class="ai-training-validation__progress-track">
            <span :style="{ width: `${progressPercentage}%` }"></span>
          </div>
        </div>

        <div class="ai-training-validation__controls">
          <div class="ai-training-validation__search">
            <i class="bx bx-search"></i>
            <b-form-input
              v-model="searchKeyword"
              placeholder="Cari nama atau nomor WO"
              @input="handleSearchInput"
            />
          </div>

          <div class="ai-training-validation__filters">
            <button
              v-for="filter in filterCards"
              :key="filter.value"
              type="button"
              class="ai-training-validation-filter"
              :class="{ 'ai-training-validation-filter--active': activeFilter === filter.value }"
              @click="changeFilter(filter.value)"
            >
              <i class="bx" :class="filter.icon"></i>
              <span>
                <strong>{{ filter.label }}</strong>
                <small>{{ filter.description }}</small>
              </span>
            </button>
          </div>
        </div>

        <b-row class="ai-training-validation__grid">
          <b-col v-if="isLoading" cols="12" class="ai-training-validation__state">
            <b-spinner small class="me-2"></b-spinner>
            Memuat data...
          </b-col>
          <b-col v-else-if="!photos.length" cols="12" class="ai-training-validation__state">
            Data belum tersedia
          </b-col>
          <template v-else>
            <b-col v-for="photo in photos" :key="photo.id" cols="12" md="4" xl="3" class="mb-3">
              <div
                class="ai-training-photo-card"
                :class="{
                  'ai-training-photo-card--valid': photo.status === 'valid',
                  'ai-training-photo-card--invalid': photo.status === 'invalid',
                }"
              >
                <div class="ai-training-photo-card__image">
                  <img v-if="photo.url" :src="photo.url" :alt="photo.name" />
                  <i v-else class="bx bx-image"></i>
                  <span
                    class="ai-training-photo-card__status"
                    :class="{
                      'ai-training-photo-card__status--valid': photo.status === 'valid',
                      'ai-training-photo-card__status--invalid': photo.status === 'invalid',
                    }"
                  >
                    <i v-if="photo.status" class="bx" :class="getStatusIcon(photo)"></i>
                  </span>
                </div>

                <div class="ai-training-photo-card__body">
                  <h5>{{ photo.name }}</h5>

                  <div class="ai-training-photo-card__actions">
                    <b-button
                      class="ai-training-photo-card__valid-btn"
                      :variant="photo.status === 'valid' ? 'primary' : 'outline-primary'"
                    >
                      <i class="bx bx-check-circle"></i>
                    </b-button>

                    <b-dropdown
                      block
                      menu-class="ai-training-invalid-menu"
                      class="ai-training-photo-card__invalid-btn"
                      :variant="photo.status === 'invalid' ? 'danger' : 'outline-primary'"
                    >
                      <template #button-content>
                        <i class="bx bx-x-circle"></i>
                        Tidak valid
                      </template>
                      <b-dropdown-item
                        v-for="reason in reasonOptions"
                        :key="reason.invalid_reason_id"
                      >
                        {{ reason.name }}
                      </b-dropdown-item>
                    </b-dropdown>
                  </div>

                  <div v-if="photo.status === 'invalid' && photo.reason" class="ai-training-photo-card__reason">
                    <i class="bx bx-edit-alt"></i>
                    <span>{{ photo.reason }}</span>
                  </div>
                  <div v-else class="ai-training-photo-card__reason-valid">
                    <i class="bx bx-edit-alt"></i>
                    <span>valid</span>
                  </div>

                  <div class="ai-training-photo-card__footer">
                    <div class="ai-training-photo-card__wo">
                      <div class="ai-training-photo-card__wo-icon">
                        <i class="mc-icon icon-outline_truck-idle"></i>
                      </div>
                      <div>
                        <p class="mb-0">{{ photo.workOrder }}</p>
                        <span>{{ photo.workOrderType }}</span>
                      </div>
                    </div>
                    <!-- <b-button
                      v-if="hasChanged(photo)"
                      size="sm"
                      variant="primary"
                      :disabled="savingIds[photo.id] || isSavingAll"
                      @click="savePhoto(photo)"
                    >
                      <b-spinner v-if="savingIds[photo.id]" small class="me-1"></b-spinner>
                      Simpan
                    </b-button> -->
                  </div>
                </div>
              </div>
            </b-col>
          </template>
        </b-row>

        <div
          v-if="photos.length"
          class="ai-training-validation__pagination"
        >
          <div class="ai-training-validation__pagination-info">
            Menampilkan
            <strong>{{ paginationStart }}</strong>
            -
            <strong>{{ paginationEnd }}</strong>
            dari
            <strong>{{ totalRows }}</strong>
            data
          </div>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            class="pagination-rounded mb-0"
            @change="changePage"
          />
        </div>
      </div>
    </div>
  </Layout>
</template>
